/**
 * What config a node requires — one definition, for every validator.
 *
 * The registry already knows. Every entry in
 * `NODE_TYPES[type].configSchema` carries `required: true|false`, and that
 * is what the Inspector renders from and what the AI generator is told to
 * satisfy. Both canvas validators nevertheless kept their own hand-written
 * table of the same thing, byte-identical to each other and drifted from the
 * registry on 4 of its 14 entries — in both directions:
 *
 *   http_task          checked [url]       schema requires [method, url]
 *   llm_task           checked [model, …]  also requires [connectorId, mode]
 *   script_task        checked [code]      also requires [language]
 *   notification_task  checked [body, …]   body is required:FALSE, and
 *                                          `channel` was never checked
 *
 * The `body` half cost real workflows. Its own help text says "auto-filled
 * when a template is selected", so the schema marks it optional — and the
 * validator demanded it anyway. Fourteen workflows were flagged, seven of
 * them with a template set, and no correct action could clear the error.
 * That is the terminal AI-fix loop RC-112a was written to end, arriving by a
 * different door: the validator asks for something the schema will not
 * accept, so the fix is rejected, so the validator asks again.
 *
 * RC-112b added a drift guard against exactly this and it did not fire,
 * because it only asserts the field NAME appears in the schema. A name can
 * be present while its `required` flag disagrees, which is what happened.
 *
 * ── WHY A FLOOR RATHER THAN A CLEAN DERIVATION ─────────────────────────
 *
 * Deriving purely from `required: true` would be the tidier code and the
 * wrong change. Five fields the schema requires have never been checked by
 * anything, and 39 saved workflows are missing at least one. Turning those
 * red in one release makes shipped, working canvases look broken for
 * something that has been true since the day they were authored.
 *
 * So the historical table below stays, as the ERROR FLOOR — the fields a
 * workflow has always been refused for — and everything the registry adds on
 * top arrives as a WARNING. Nothing that validates today starts failing;
 * nothing the registry requires goes unmentioned. Promote warnings to errors
 * once the corpus is clean.
 *
 * A field in the floor that the registry marks OPTIONAL is dropped outright.
 * The registry wins — it is the one the Inspector and the generator read.
 */

import { NODE_TYPES } from '../../data/soarNodes';

/* Per-node-type config fields that have always been hard errors.
 *
 * RC-112a — field names AUDITED against soarNodes.js configSchemas.
 * Pre-RC-112a values were drifted from the schemas (`legs` vs `branches`,
 * `approverRoles` vs `approvers`, `prompt` vs `userPromptTemplate`,
 * `workerKey` vs `taskDefName`, `recipient` vs `recipients`, `connector` vs
 * `connectorId`, `policy_gate` not even a real node type).
 *
 * `a|b` means "at least one of these keys is present and non-empty" — used
 * where the platform accepts two spellings (RC-27: terminate's status key).
 */
export const LEGACY_REQUIRED_CONFIG = Object.freeze({
  http_task:         ['url'],
  llm_task:          ['model', 'userPromptTemplate'],
  worker_task:       ['taskDefName'],
  condition:         ['expression'],
  switch_case:       ['expression', 'cases'],
  script_task:       ['code'],
  notification_task: ['recipients', 'body'],
  parallel_fork:     ['branches'],
  fork:              ['branches'],   // paletteHidden alias of parallel_fork
  approval_gate:     ['approvers', 'message'],
  human_task:        [],             // approvers is optional in the schema
  form_task:         ['formSchema'],
  connector_task:    ['connectorId', 'action'],
  terminate:         ['terminationStatus|status'],
});

/**
 * This codebase's accessor for a schema field's name.
 *
 * Three node types (end, terminate, do_while) spell it `key` while the other
 * 81 spell it `field`. Reading only one of them makes a required field
 * invisible — `do_while.loopCondition` is required and was checked by
 * nothing. The RC-112b drift guard already reads both; so does this.
 */
export function schemaFieldName(entry) {
  return (entry && (entry.field || entry.key)) || null;
}

/** Empty string, empty array and empty object all count as "not filled in". */
export function isConfigMissing(value) {
  if (value == null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return true;
  return false;
}

/**
 * The config fields to check for a node type, each with the severity its
 * absence should carry.
 *
 * @returns {Array<{spec: string, severity: 'error'|'warning'}>|null}
 *   null when the type has nothing to check.
 */
export function requiredConfigFor(type) {
  const legacy = LEGACY_REQUIRED_CONFIG[type];
  const schema = (NODE_TYPES[type] && NODE_TYPES[type].configSchema) || [];
  if (!legacy && schema.length === 0) return null;

  const findField = (name) => schema.find((f) => schemaFieldName(f) === name);
  const out = [];
  const seen = new Set();

  for (const spec of legacy || []) {
    const alternatives = String(spec).split('|').map((x) => x.trim()).filter(Boolean);
    // An OR-form, or a key the schema does not model, keeps its historical
    // error severity — the schema cannot speak to it either way.
    const modelled = alternatives.length === 1 ? findField(alternatives[0]) : null;
    if (modelled && modelled.required !== true) continue; // schema says optional
    out.push({ spec, severity: 'error' });
    alternatives.forEach((a) => seen.add(a));
  }

  for (const f of schema) {
    const name = schemaFieldName(f);
    if (!name || f.required !== true || seen.has(name)) continue;
    out.push({ spec: name, severity: 'warning' });
    seen.add(name);
  }

  return out.length ? out : null;
}

/**
 * The fields a node is actually missing.
 *
 * Both validators call this rather than walking the table themselves. The
 * previous arrangement had v3 handling the `a|b` OR-form and v4 not, so
 * v4Validate reported every `terminate` node as missing a field literally
 * named "terminationStatus|status". One implementation cannot disagree with
 * itself.
 *
 * @returns {Array<{field: string, severity: 'error'|'warning'}>}
 */
export function missingRequiredConfig(node) {
  const required = requiredConfigFor(node && node.type);
  if (!required) return [];
  const cfg = (node && node.config) || {};
  const missing = [];
  for (const { spec, severity } of required) {
    const alternatives = String(spec).split('|').map((x) => x.trim()).filter(Boolean);
    const anySet = alternatives.some((alt) => !isConfigMissing(cfg[alt]));
    if (!anySet) missing.push({ field: alternatives[0], severity });
  }
  return missing;
}
